import React, { Component } from 'react';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';
import { graphql } from 'react-apollo';
import { func, object, arrayOf, bool, number } from 'prop-types';
import { store } from 'store';
import { compose } from 'redux';
import { locationsimple as Location } from 'common/svg/icons';
import { ROUTE_STORAGE, DRAWING_ZOOM_LEVEL } from 'common/constants';
import { FETCH_STORAGE, FETCH_ALL_STORAGES } from 'api/storage';
import { base64_decode } from 'util/base64';
import { change } from 'redux-form';
import { StorageForm } from 'containers';
import { uniqBy } from 'common/utils';
import {
  LevelNav,
  ErrorBoundary,
  FieldWrapper,
  Button,
} from 'common/components';
import { toggleFieldView } from 'actions/field';
import addStorageGif from 'common/gifs/add-storage.gif';

export class StorageEdit extends Component {
  static displayName = 'StorageEdit';

  static propTypes = {
    browser: object,
    closeForm: func,
    match: object,
    storages: arrayOf(object),
    storage: object,
    toggleFieldView: func.isRequired,
    showMapFlag: bool.isRequired,
    zoom: number.isRequired,
    resetStorageForm: func.isRequired,
  };

  state = {
    showStorageForm: false,
    markerDropped: false,
  };

  componentWillUnmount() {
    // destory storage form
    this.props.resetStorageForm();
  }

  render() {
    const {
      browser,
      closeForm,
      match: {
        params: { encodedId },
      },
      storages,
      storage,
      toggleFieldView,
      showMapFlag,
      zoom,
    } = this.props;
    const { showStorageForm } = this.state;
    const ID = (encodedId && base64_decode(encodedId)) || 'add';
    const mode = ID !== 'add' ? 'edit' : 'add';
    let location;
    if (storage && storage.location) {
      location = { lat: storage.location.lat, lng: storage.location.lng };
    }
    const existingStorageNames = storages
      ? storages.map(storage => storage.name)
      : [];
    const existingStorageBinYards = storages
      ? storages
          .filter(storage => !!storage.binYard)
          .map(storage => storage.binYard)
      : [];
    const binYardOptions = uniqBy(
      [
        { label: 'None', value: 'NONE', default: true },
        ...existingStorageBinYards.map(yard => ({ label: yard, value: yard })),
      ],
      'value'
    );
    const enableDropping = zoom >= DRAWING_ZOOM_LEVEL;
    const { header, button } = this._getMobileUI(enableDropping);

    return (
      <ErrorBoundary>
        <FieldWrapper
          theme={['white']}
          showMapFlag={(!showStorageForm && mode === 'add') || showMapFlag}
          useMaxZoomService
          usePreservedLocation
          storage={storage}
          mapOptions={{
            onLocationUpdate: location => this._setFieldLocaiton(location),
            markerDraggable: true,
            marker: location,
            withCurrentLocation: true,
            allowMarkerDrop: mode === 'add' && enableDropping,
            pinIcon: 'storage',
            key: 'viewmode',
            useMaxZoomService: true,
            control: {
              position: 'BOTTOM_CENTER',
              component: browser.is.mobile ? button : null,
            },
          }}
          nav={
            <LevelNav
              hideRightIcon
              nav={{
                left: {
                  label: <span className={'visible--desktop'}>Back</span>,
                  action: closeForm,
                },
                center: {
                  label:
                    browser.is.mobile && mode !== 'edit'
                      ? header
                      : `${mode} Storage`,
                },
              }}
            />
          }
        >
          {showStorageForm || mode !== 'add' ? (
            <StorageForm
              id={ID}
              location={location}
              closeForm={closeForm}
              existingStorageNames={existingStorageNames}
              binYardOptions={binYardOptions}
              storage={storage}
              toggleFieldView={toggleFieldView}
            />
          ) : (
            browser.greaterThan.mobile && this._getLeftPanel(enableDropping)
          )}
        </FieldWrapper>
      </ErrorBoundary>
    );
  }

  _getLeftPanel = enableDropping => {
    const { markerDropped } = this.state;
    return enableDropping || markerDropped ? (
      <div className="addfield__container addfield__container--tight">
        <h3 className="text--header text--medium">Drop a pin</h3>
        <p className="text--bold text--medium addfield__content">
          Tap on the map to drop the pin on your storage location. Change the
          location of the pin by dragging it to a new location.
        </p>
        <div className="text--center">
          <div>
            <img src={addStorageGif} className="addfield__gif" />
          </div>
          {markerDropped && (
            <div className={'field__group'}>
              <fieldset className={'field text--center'}>
                <Button
                  className="btn btn--primary"
                  onClick={this._showStorageForm}
                >
                  Done
                </Button>
              </fieldset>
            </div>
          )}
        </div>
      </div>
    ) : (
      <div className="addfield__container">
        <h3 className="text--header text--medium">Locate your Storage</h3>
        <p className="text--bold text--medium addfield__content">
          Locate your storage by scrolling & zooming through the map on the
          right. You can also click on the GPS icon{' '}
          <span>
            <Location className="addfield__location" />
          </span>
          to zoom into your current location.
        </p>
        <div className="text--center">
          <Button className="btn btn--disabled">Zoom In More</Button>
        </div>
      </div>
    );
  };

  _getMobileUI = enableDropping => {
    const { toggleFieldView, showMapFlag } = this.props;
    const { markerDropped, showStorageForm } = this.state;

    let header = 'LOCATE YOUR STORAGE';
    let button = {
      className: 'btn--disabled',
      text: 'ZOOM IN MORE',
    };

    if (enableDropping) {
      header = 'DROP A PIN';
      button = {};
    }

    if (markerDropped || showMapFlag) {
      header = 'DROP A PIN';
      button = {
        onClick: () => {
          this._showStorageForm();
          if (showMapFlag) {
            toggleFieldView();
          }
        },
        text: 'DONE',
      };

      if (showStorageForm && !showMapFlag) {
        header = 'ADD STORAGE';
        button = {};
      }
    }

    const { className = '', ...buttonProps } = button;
    const buttonComponent = Object.entries(button).length > 0 && (
      <Button
        className={`btn addfield__button ${className}`}
        {...buttonProps}
      />
    );

    return {
      header,
      button: buttonComponent,
    };
  };

  _showStorageForm = () => this.setState({ showStorageForm: true });

  _setFieldLocaiton = location => {
    store.dispatch(
      change('storageform', 'location', `${location.lat}, ${location.lng}`)
    );
    if (!this.state.markerDropped) {
      this.setState({ markerDropped: true });
    }
  };
}
const mapState = ({ browser, field: { showMapFlag }, map: { zoom } }) => ({
  browser,
  showMapFlag,
  zoom,
});

const mapDispatch = dispatch => ({
  closeForm: () => {
    dispatch(toggleFieldView({ isOpen: false }));
    dispatch(push(ROUTE_STORAGE));
  },
  resetStorageForm: () =>
    dispatch({
      type: '@@redux-form/DESTROY',
      meta: { form: ['storageform'] },
    }),
  toggleFieldView: () => dispatch(toggleFieldView()),
});

const enhance = compose(
  graphql(FETCH_STORAGE, {
    options: ({
      match: {
        params: { encodedId },
      },
    }) => ({
      variables: { id: encodedId && base64_decode(encodedId) },
      fetchPolicy: 'cache-and-network',
    }),
    props: props => {
      if (!props.data || !props.data.getStorage) {
        return;
      }

      return {
        storage: props.data.getStorage,
        ...props,
      };
    },
  }),
  graphql(FETCH_ALL_STORAGES, {
    options: () => ({
      variables: { active: true },
      fetchPolicy: 'cache-and-network',
    }),
    props: props => {
      if (!props.data || !props.data.getAllStorages) {
        return;
      }

      return {
        storages: props.data.getAllStorages,
        ...props,
      };
    },
  }),

  connect(
    mapState,
    mapDispatch
  )
);
export default enhance(StorageEdit);



// WEBPACK FOOTER //
// ./src/pages/Storage/Edit/index.js